"use client";

import React from "react";
import { Calculator, Atom, Terminal, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const subjects = [{
  id: "strategy",
  title: "Business Strategy",
  description: "Architecting high-level roadmaps that align organizational goals with market opportunities for sustainable, long-term growth.",
  icon: Calculator,
  iconColor: "text-primary",
  iconBg: "bg-primary/10",
  link: "/StrategicPillarsPage",
  isCoding: false
}, {
  id: "digital",
  title: "Digital Transformation",
  description: "Modernizing legacy systems and workflows through cutting-edge technology integration to enhance operational efficiency.",
  icon: Atom,
  iconColor: "text-secondary",
  iconBg: "bg-secondary/10",
  link: "/StrategicPillarsPage",
  isCoding: false
}, {
  id: "ai-solutions",
  title: "AI Solutions",
  description: "Deploying intelligent automation and predictive analytics to solve complex business challenges and unlock hidden data value.",
  icon: Terminal,
  iconColor: "text-primary",
  iconBg: "bg-primary/10",
  link: "/StrategicPillarsPage",
  isCoding: true
}];
const TerminalEffect = () => <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.04] dark:opacity-[0.08] font-mono text-xs leading-relaxed text-foreground select-none z-0 p-6 flex flex-col">
    <div className="animate-pulse delay-75">{`> INITIALIZING FMC_CORE...`}</div>
    <div className="animate-pulse delay-150">{`> LOADING MODULES: [STRATEGY, DIGITAL, AI]`}</div>
    <div className="animate-pulse delay-300">{`> OPTIMIZING GROWTH PATHS...`}</div>
    <div className="animate-pulse delay-500">{`> SYSTEM READY.`}</div>
    <div className="mt-4 animate-pulse delay-700">{`> function innovate() {`}</div>
    <div className="animate-pulse delay-1000">{`    while(market_evolves > 0) {`}</div>
    <div className="animate-pulse delay-1000">{`      analyze();`}</div>
    <div className="animate-pulse delay-1000">{`      transform();`}</div>
    <div className="animate-pulse delay-1000">{`    }`}</div>
    <div className="animate-pulse delay-700">{`  }`}</div>
  </div>;
export default function StrategicPillars() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="265264" id="strategic-pillars" className="relative w-full py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMDEiLz4KPHBhdGggZD0iTTAgMGgxdjFIMHptMiAyaDF2MUgyeiIgZmlsbD0iIzAwMCIgZmlsbC1vcGFjaXR5PSIwLjAyIi8+Cjwvc3ZnPg==')] z-0" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tighter">
            Strategic Pillars
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Future Mind Consulting delivers premium expertise across five core domains to drive innovation and professional excellence.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {subjects.map((subject, i) => <div data-index={i} key={subject.id} className="h-full">
              <Card className="relative overflow-hidden group h-full min-h-[420px] flex flex-col bg-card/60 backdrop-blur-xl border-white/10 shadow-lg hover:shadow-3xl transition-all duration-500 rounded-3xl z-10">
                {}
                {subject.isCoding && <TerminalEffect />}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 pointer-events-none z-0 transition-opacity duration-500 group-hover:opacity-100" />

                <CardHeader className="relative z-10 flex-1 pb-0 px-8 pt-8">
                  <div className="flex justify-between items-start w-full h-full flex-col">
                    <div className={cn("absolute top-8 right-8 p-4 rounded-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-md", subject.iconBg)}>
                      <subject.icon className={cn("size-10", subject.iconColor)} strokeWidth={1} />
                    </div>
                    <div className="mt-auto pt-24">
                      <CardTitle className="text-3xl font-default font-bold mb-4 text-foreground tracking-tight">
                        {subject.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground font-default leading-relaxed">
                        {subject.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardFooter className="relative z-10 px-8 pt-8 pb-8 mt-auto">
                  <Button variant="ghost" className="p-0 hover:bg-transparent text-primary font-bold group/btn h-auto text-base uppercase tracking-widest" asChild>
                    <Link to={subject.link}>
                      Explore Service
                      <ArrowRight className="ml-2 size-5 transition-transform duration-300 -translate-x-2 opacity-0 group-hover/btn:translate-x-0 group-hover/btn:opacity-100" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}